<script setup lang="ts">
import { nextTick, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import axios from '@/api/axios'
import API_ENDPOINTS from '@/api/client'
import { track } from '@/boot/mixpanel'
import { useSectionTour } from '@/composables/useSectionTour'
import LogisticsShippingOrder from '@/views/apps/logistics/LogisticsShippingOrder.vue'

definePage({
  meta: {
    authenticatedOnly: true,
  },
})

const { t } = useI18n()
const auth = JSON.parse(localStorage.getItem('auth') || '{}')
const accessToken = auth?.access_token || ''
const user_id = auth?.user?.id || ''
const filter_pending = 'PENDING'
const filter_completed = 'COMPLETED'

const contractsObject = ref<any[]>([])
const filterType = ref(filter_pending)
const alert = ref<{ type: string; message: string } | null>(null)
const alertTimeout = ref<number | null>(null)

const selectedStatus = ref<string | null>(null)
const startDate = ref<string | null>(null)
const endDate = ref<string | null>(null)
const isMobile = ref(false)

const statusOptions = [
  { text: t('All'), value: null },
  { text: t('Operation at origin'), value: 'ORIGIN' },
  { text: t('Operation at destination'), value: 'DESTINATION' },
]

const fetchContracts = async () => {
  try {
    const url = API_ENDPOINTS.GET_CONTRACTS_LOGISTIC(user_id, filterType.value)

    const res = await axios.get(url, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    })

    contractsObject.value = res.data.map((contract: any) => ({
      id: contract.id,
      contract_id: contract.contract_id,
      pickup_address: contract.pickup_address,
      product: {
        name: contract.product?.meta_product?.name || 'N/A',
        family: contract.product?.family?.name || 'N/A',
        variety: contract.product?.variety?.name || 'N/A',
      },
      company_buyer: contract.company_buyer,
      company_seller: contract.company_seller,
      required_shipments: contract.required_shipments,
      amount: contract.amount,
      pickup_date_from: contract.pickup_date_from,
      pickup_date_to: contract.pickup_date_to,
      is_buyer: contract.is_buyer,
      is_seller: contract.is_seller,
      status: contract.status,
      is_destination: contract.is_destination,
    }))
  }
  catch (error) {
    console.error('Error fetching contracts:', error)
  }
}

watch(filterType, fetchContracts)

watch(selectedStatus, () => {
  track('Selected operations filter')
})

watch(startDate, () => {
  track('Changed Start Due Date filter')
})

watch(endDate, () => {
  track('Changed End Due Date filter')
})

const showAlert = (type: string, message: string, duration: number = 10000) => {
  alert.value = { type, message }

  nextTick(() => {
    setTimeout(() => {
      const alertElement = document.querySelector('.v-alert__content')

      if (alertElement) {
        const elementPosition = alertElement.getBoundingClientRect().top + window.scrollY
        const fixedHeaderHeight = 250
        const scrollToPosition = elementPosition - fixedHeaderHeight

        window.scrollTo({ top: scrollToPosition, behavior: 'smooth' })
      }
    }, 100)
  })

  if (alertTimeout.value)
    clearTimeout(alertTimeout.value)

  alertTimeout.value = setTimeout(() => {
    alert.value = null
  }, duration) as unknown as number
}

const filterContractsByDate = (contracts: any[]) => {
  return contracts.filter(contract => {
    const pickupFrom = contract.pickup_date_from ? new Date(contract.pickup_date_from) : null
    const pickupTo = contract.pickup_date_to ? new Date(contract.pickup_date_to) : null

    const matchesStartDate = startDate.value ? (pickupFrom && pickupFrom >= new Date(startDate.value)) : true
    const matchesEndDate = endDate.value ? (pickupTo && pickupTo <= new Date(endDate.value)) : true

    return matchesStartDate && matchesEndDate
  })
}

const filteredContracts = computed(() => {
  let filtered = contractsObject.value

  if (startDate.value || endDate.value)
    filtered = filterContractsByDate(filtered)

  if (selectedStatus.value === 'DESTINATION')
    filtered = filtered.filter(contract => contract.is_destination === true)
  else if (selectedStatus.value === 'ORIGIN')
    filtered = filtered.filter(contract => contract.is_destination === false)

  return filtered
})

const handleContractCompletion = (contractId: string, id: number) => {
  contractsObject.value = contractsObject.value.filter(contract => contract.id !== id)

  showAlert('success', t('contractCompleted', { contractId }))
}

const handleShippingOrderCancelation = (contractId: string, id: number) => {
  contractsObject.value = contractsObject.value.filter(contract => contract.id !== id)

  showAlert('success', t('shippingOrderCancelled', { contractId }))
}

const { startIfFirstTime } = useSectionTour('shipping-orders', 'v1', [
  {
    selector: '[data-tour="shipping-orders-tabs"]',
    title: t('tutorials.shippingOrders.step1.title'),
    text: t('tutorials.shippingOrders.step1.text'),
    position: 'bottom',
  },
  {
    selector: '[data-tour="shipping-orders-filters"]',
    title: t('tutorials.shippingOrders.step2.title'),
    text: t('tutorials.shippingOrders.step2.text'),
    position: 'bottom',
  },
  {
    selector: '[data-tour="shipping-orders-list"]',
    title: t('tutorials.shippingOrders.step3.title'),
    text: t('tutorials.shippingOrders.step3.text'),
    position: 'top',
  },
  {
    selector: '[data-tour="shipping-orders-actions"]',
    title: t('tutorials.shippingOrders.step4.title'),
    text: t('tutorials.shippingOrders.step4.text'),
    position: 'top',
  },
])

onMounted(fetchContracts)
onMounted(() => {
  isMobile.value = window.innerWidth <= 1280
  window.addEventListener('resize', () => {
    isMobile.value = window.innerWidth <= 1280
  })
  startIfFirstTime()
})
</script>

<template>
  <VRow class="match-height">
    <VCol cols="12 mb-4">
      <VTabs
        v-model="filterType"
        data-tour="shipping-orders-tabs"
      >
        <VTab :value="filter_pending">
          {{ t("Pending") }}
        </VTab>
        <VTab :value="filter_completed">
          {{ t("CompletedTag") }}
        </VTab>
      </VTabs>
      <div data-tour="shipping-orders-filters">
        <div
          v-if="!isMobile"
          class="filter-container"
        >
          <VSelect
            v-model="selectedStatus"
            :items="statusOptions"
            item-title="text"
            item-value="value"
            :label="t('Destination/Origin')"
            dense
            hide-details
          />
          <div class="date-range-filter">
            <VTextField
              v-model="startDate"
              :label="t('Start Due Date')"
              type="date"
              dense
              hide-details
            />
            <VTextField
              v-model="endDate"
              :label="t('End Due Date')"
              type="date"
              dense
              hide-details
            />
          </div>
        </div>
        <div v-else>
          <div
            class="filter-container"
            style=" margin-block: 16px 8px;"
          >
            <VSelect
              v-model="selectedStatus"
              :items="statusOptions"
              item-title="text"
              item-value="value"
              :label="t('Destination/Origin')"
              dense
              hide-details
            />
          </div>
          <div
            class="date-range-filter"
            style="margin-block-end: 20px;"
          >
            <VTextField
              v-model="startDate"
              :label="t('Start Due Date')"
              type="date"
              dense
              hide-details
            />
            <VTextField
              v-model="endDate"
              :label="t('End Due Date')"
              type="date"
              dense
              hide-details
            />
          </div>
        </div>
      </div>

      <VAlert
        v-if="alert"
        :type="alert.type"
        variant="tonal"
        class="mb-4"
      >
        {{ alert.message }}
      </VAlert>
      <div data-tour="shipping-orders-list">
        <div
          v-if="contractsObject.length === 0"
          class="text-center py-4"
        >
          <p>{{ t('No contracts available') }}</p>
        </div>
        <div
          v-for="(contract, index) in filteredContracts"
          v-else
          :key="contract.id"
          :data-tour="index === 0 ? 'shipping-orders-actions' : undefined"
        >
          <LogisticsShippingOrder
            :contract="contract"
            :is-first="index === 0"
            @contract-completed="handleContractCompletion"
            @shipping-order-cancelled="handleShippingOrderCancelation"
          />
          <VDivider class="my-4" />
        </div>
      </div>
    </VCol>
  </VRow>
</template>

<style scoped>
.filter-container {
  display: flex;
  justify-content: space-between;
  margin-block: 20px 20px;
}

.date-range-filter {
  display: flex;
  gap: 10px;
}
</style>
